import React, { useContext, useEffect } from "react";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { setPageName } from "../../../actions";
import { DashboardLayout } from "../../../components/Layouts";
import { DefaultPageContent } from "../../../components/PageContent";
import { DEALER_DEEP_DIVE_METRICS, MODEL_DEEP_DIVE_METRICS, VOC_BTN_FILTERS } from "../../../constants/vocConstants";
import { PageTitle } from "../../../components/PageContent/subs";
import { DashboardBannerContext } from "../../../context";
import VocQuickAfterSalesFilters from "../../../components/Filters/Voc/VocQuickAfterSalesFilters";
import "../../../assets/styles/pages/quickVocSales.scss";
import { useQuery } from "@tanstack/react-query";
import PerformanceByMarket from "../QuickVocSales/components/PerformanceByMarket";
import MarketDeepDive from "../QuickVocSales/components/MarketDeepDive";
import {
  getDealerDeepDiveTable,
  getDealerOverallPerformanceTrend,
  getDealerScoreDistribution,
  getMarketDeepDiveTable,
  getMarketDeepDiveTrendline,
  getModelDeepDiveTable,
  getModelOverallPerformanceTrend,
  getModelScoreDistribution,
  getModelTypeDeepDiveTable,
  getModelTypeOverallPerformanceTrend,
  getModelTypeScoreDistribution,
  getPerformanceByMarketMap,
  getQuickVocAfterSalesFilters,
  getQuickVocAfterSalesLastRefreshDate,
} from "../../../api/quickVocAfterSales";
import DeepDiveBreakdown from "../QuickVocSales/components/DeepDiveBreakdown";
import { RouteComponentProps, withRouter } from "react-router-dom";
import TopicalClassification from "../QuickVocSales/components/TopicalClassification";
import VerbatimTable from "../QuickVocSales/components/VerbatimTable";
import {
  fetchVocAfterSalesTopicClassificationDissatisfaction,
  fetchVocAfterSalesTopicClassificationSatisfaction,
} from "../../../actions/vocActions";

const QuickVocAfterSales = withRouter((props: RouteComponentProps) => {
  const dispatch = useDispatch();

  const { history } = props;

  const { setBannerText, setShowBanner } = useContext(DashboardBannerContext);
  // Route access (PrivateRoute's own "voc" view check) is broader than data access here - a user
  // can reach this page and still have an individual request 403 (e.g. a market/brand-scoped VOC
  // permission the coarse "voc" view doesn't capture), which used to leave every tile on the page
  // quietly rendering "No data available" with no indication it's a permissions issue rather than
  // an empty result. Same convention as MsrpDashboard.tsx's own canAccessView/banner handling.
  const canAccessView = useSelector((state: RootStateOrAny) => state.page.can_access_view);

  const { data: lastDataRefreshData, refetch: refetchLastDataRefresh } = useQuery({
    queryKey: ["quickVocAfterSalesLastDataRefresh"],
    queryFn: getQuickVocAfterSalesLastRefreshDate,
    enabled: false,
  });

  // state.page.pageName (what PageTitle actually renders) is global, not page-scoped - without
  // setting it here it keeps whatever the last VOC page that DID set it left behind (e.g. sticks
  // on "VOC (Voice Of Customer)" from Website.tsx after navigating here via the top tabs).
  useEffect(() => {
    dispatch(setPageName("Quick VOC After Sales"));
  }, [dispatch]);

  useEffect(() => {
    refetchLastDataRefresh();
  }, [history.location.search]);

  useEffect(() => {
    dispatch(fetchVocAfterSalesTopicClassificationDissatisfaction());
    dispatch(fetchVocAfterSalesTopicClassificationSatisfaction());
  }, [history.location.search, dispatch]);

  useEffect(() => {
    if (!canAccessView) {
      setShowBanner(true);
      setBannerText(
        "Quick VOC After Sales data is not available for the selected market/brand. If you believe this is an error, please contact the CEDAR Team."
      );
    }
    return () => {
      setShowBanner(false);
    };
  }, [canAccessView]);

  return (
    <DashboardLayout>
      <DefaultPageContent
        filter={
          <VocQuickAfterSalesFilters filterBtns={VOC_BTN_FILTERS} queryFn={getQuickVocAfterSalesFilters} pageName="Quick VOC After Sales" />
        }
        dataDocumentation="voc"
        lastDataRefresh={lastDataRefreshData?.date ?? ""}
        pageTitle={<PageTitle dataDocumentation="voc" />}
      >
        <div className="quick-voc-sales">
          <PerformanceByMarket queryFn={getPerformanceByMarketMap} />
          <MarketDeepDive tableQueryFn={getMarketDeepDiveTable} trendlineQueryFn={getMarketDeepDiveTrendline} />
          <DeepDiveBreakdown
            mainTitle="Dealer Deep Dive"
            overallTableTitle="Dealers overall performance"
            overtimeTrendTitle="Dealers overtime performance"
            volumeOfResponsesTitle="Dealers score distribution"
            metrics={DEALER_DEEP_DIVE_METRICS}
            metric="dealer"
            tableQueryFn={getDealerDeepDiveTable}
            trendQueryFn={getDealerOverallPerformanceTrend}
            distributionQueryFn={getDealerScoreDistribution}
          />
          <DeepDiveBreakdown
            mainTitle="Model Deep Dive"
            overallTableTitle="Models overall performance"
            overtimeTrendTitle="Models overtime performance"
            volumeOfResponsesTitle="Models score distribution"
            metrics={MODEL_DEEP_DIVE_METRICS}
            metric="model"
            tableQueryFn={getModelDeepDiveTable}
            trendQueryFn={getModelOverallPerformanceTrend}
            distributionQueryFn={getModelScoreDistribution}
          />
          <DeepDiveBreakdown
            mainTitle="Model Type Deep Dive"
            overallTableTitle="Model type overall performance"
            overtimeTrendTitle="Model type overtime performance"
            volumeOfResponsesTitle="Model type score distribution"
            metric="model_type"
            tableQueryFn={getModelTypeDeepDiveTable}
            trendQueryFn={getModelTypeOverallPerformanceTrend}
            distributionQueryFn={getModelTypeScoreDistribution}
          />
          <div className="vocPage">
            <TopicalClassification />
            <VerbatimTable />
          </div>
        </div>
      </DefaultPageContent>
    </DashboardLayout>
  );
});

export default QuickVocAfterSales;
